import {Chip} from '@dropbox/dig-components/chip';
import {UIIcon} from '@dropbox/dig-icons';
import {OpenLine} from '@dropbox/dig-icons/assets';
import {useQuery} from '@tanstack/react-query';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {aiDrawerViewAtom, aiSearchComposerDraftAtom, openAiSearchAndAskAtom} from 'atoms/aiSearch';
import {showSearchAtom} from 'atoms/search';
import {HomeService, KnowledgeLink} from 'client';
import {t} from 'i18next';
import {useSetAtom} from 'jotai';
import {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router';
import {getService} from 'utilities';
import {HOME_KNOWLEDGE_LINKS_QUERY_KEY} from 'views/home/homeQueryKeys';
import {
  askChipAction,
  knowledgeChipPageId,
  knowledgeChipQuestion,
} from 'views/home/sections/Search';

import styles from './SearchBox.module.css';

/**
 * Chips that wrap to at most two lines, with the rest behind "+N". It measures
 * after each layout and drops a chip until the "+N" fits on the second line.
 */
const TwoLineChips = ({
  links,
  onOpen,
  all = false,
}: {
  links: KnowledgeLink[];
  onOpen: (link: KnowledgeLink) => void;
  /** Every chip, no "+N" (there's room for them). */
  all?: boolean;
}) => {
  const [expanded, setShowAll] = useState(false);
  const showAll = all || expanded;
  const [fit, setFit] = useState(links.length);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => setFit(links.length), [links.length]);
  useLayoutEffect(() => {
    if (showAll || !ref.current) return;
    const tops = new Set(
      Array.from(ref.current.children).map((child) => (child as HTMLElement).offsetTop)
    );
    if (tops.size > 2 && fit > 1) setFit((count) => count - 1);
  }, [showAll, fit, links]);
  const shown = showAll ? links : links.slice(0, fit);
  const hidden = links.length - shown.length;

  return (
    <div ref={ref} className={styles.chipWrap}>
      {shown.map((link) => {
        const question = link.link_type === 'ask_query' ? link.title : knowledgeChipQuestion(link);
        // A chip that asks stays here; one that opens a page elsewhere says so.
        const external = !question && /^https?:\/\//.test(link.url);
        return (
          <Chip key={link.id} size="small" onClick={() => onOpen(link)}>
            <Chip.Content>{link.title}</Chip.Content>
            {external && (
              <Chip.IconAccessory>
                <UIIcon src={OpenLine} aria-label={t('search_knowledge_opens_new_tab')} />
              </Chip.IconAccessory>
            )}
          </Chip>
        );
      })}
      {hidden > 0 && (
        <Chip
          size="small"
          aria-label={t('search_popular_more', {count: hidden})}
          onClick={() => setShowAll(true)}
        >
          <Chip.Content>+{hidden}</Chip.Content>
        </Chip>
      )}
    </div>
  );
};

/**
 * The home page's topic chips and what clicking one does: a knowledge-page
 * chip asks Ask with that page's id so it answers from the page's articles;
 * other chips open their link.
 */
const useTopicChips = () => {
  const navigate = useNavigate();
  const openAiSearchAndAsk = useSetAtom(openAiSearchAndAskAtom);
  const setAiDrawerView = useSetAtom(aiDrawerViewAtom);
  const setComposerDraft = useSetAtom(aiSearchComposerDraftAtom);
  const setShowSearch = useSetAtom(showSearchAtom);
  const {data: links = []} = useQuery({
    queryKey: HOME_KNOWLEDGE_LINKS_QUERY_KEY,
    queryFn: () => getService(HomeService).knowledgeLinksApiV1HomeKnowledgeLinksGet(),
    staleTime: 5 * 60 * 1000,
  });
  const chips = links.filter((link) => link.title && (link.link_type === 'ask_query' || link.url));
  const open = (link: KnowledgeLink) => {
    const isAskQuery = link.link_type === 'ask_query';
    const question = isAskQuery ? undefined : knowledgeChipQuestion(link);
    analyticsLogger().logEvent('HOME_RECENT_SEARCH', {
      chipType: link.chip_type,
      source: 'knowledge_chip',
      surface: 'search_box',
      linkType: link.link_type ?? 'url',
      action: isAskQuery || question ? 'ask' : 'link',
    });
    if (isAskQuery) {
      // Same as the home chips: "Who owns…?" starts a draft, a full question asks.
      const action = askChipAction(link.title);
      if (action.kind === 'ask') {
        openAiSearchAndAsk(action.question, 'search_knowledge_chip');
        return;
      }
      if (action.kind === 'seed') {
        setComposerDraft(action.seed);
      }
      setShowSearch(false);
      setAiDrawerView('expanded');
    } else if (question) {
      openAiSearchAndAsk(question, 'search_knowledge_chip', {
        knowledgePageId: knowledgeChipPageId(link.url),
      });
    } else if (/^https?:\/\//.test(link.url)) {
      window.open(link.url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(link.url);
    }
  };
  return {chips, open};
};

/** The home page's topic chips, two lines, then "+N"; with `all`, every one. */
export const TopicChips = ({all = false}: {all?: boolean}) => {
  const {chips, open} = useTopicChips();
  return chips.length > 0 ? <TwoLineChips links={chips} onOpen={open} all={all} /> : null;
};
